import React, { useEffect, useMemo, useState } from "react";
import { NextPage } from "next";
import { useLazyQuery, useMutation } from "@apollo/react-hooks";
import dot from "dot-object";
import { validate as validateEmail } from "email-validator";
import Input from "../../components/form/Input";
import Select from "../../components/form/Select";
import Button from "../../components/form/Button";
import { useUserContext } from "../../contexts/UserContext";
import { withApollo } from "../../components/WithApollo";
import useFormHook from "../../hooks/useForm";
import RepairPiece from "../../components/RepairPiece";
import gql from "graphql-tag";
import { useRepairContext } from "../../contexts/RepairContext";
import { GrAdd } from "react-icons/gr";
import Checkbox from "../../components/form/Checkbox";
import Textarea from "@/components/form/Textarea";
import { getThemeFromDomain } from "../_app";
import { PostHogProvider } from "posthog-js/react";

const USER_EXISTS = gql`
  mutation CheckUser($email: String!) {
    userExists(email: $email) {
      __typename
      exists
    }
  }
`;

export const CREATE_USER = gql`
  mutation CREATE_USER(
    $firstName: String!
    $lastName: String!
    $office: String!
    $email: String!
    $phone: String!
    $street: String!
    $city: String!
    $country: String!
    $state: String!
    $zip: String!
    $referred: String
    $isDesos: Boolean!
    $isColosseum: Boolean!
  ) {
    insert_user(
      objects: {
        city: $city
        country: $country
        email: $email
        firstName: $firstName
        lastName: $lastName
        office: $office
        phone: $phone
        street: $street
        zip: $zip
        state: $state
        referred: $referred
        isDesos: $isDesos
        isColosseum: $isColosseum
      }
    ) {
      __typename
    }
  }
`;

const CREATE_REPAIR = gql`
  mutation MyMutation(
    $repairPieces: repair_piece_arr_rel_insert_input!
    $discountCode: String
    $userEmail: String!
    $acceptedTerms: Boolean!
    $acceptedDsgvo: Boolean!
    $isReclamation: Boolean
    $sendEmail: Boolean
    $reclamationRepairNumber: String
    $additionalInfo: String
  ) {
    insert_repair(
      objects: {
        discountCode: $discountCode
        acceptedTerms: $acceptedTerms
        acceptedDsgvo: $acceptedDsgvo
        isReclamation: $isReclamation
        sendEmail: $sendEmail
        reclamationRepairNumber: $reclamationRepairNumber
        userEmail: $userEmail
        repairPieces: $repairPieces
        additionalInfo: $additionalInfo
      }
    ) {
      __typename
      returning {
        id
      }
    }
  }
`;

const CREATE_REPAIR_USER = gql`
  mutation MyMutation(
    $repairPieces: repair_piece_arr_rel_insert_input!
    $discountCode: String
    $userEmail: String!
    $acceptedTerms: Boolean!
    $acceptedDsgvo: Boolean!
    $isReclamation: Boolean
    $reclamationRepairNumber: String
    $additionalInfo: String
  ) {
    insert_repair(
      objects: {
        discountCode: $discountCode
        acceptedTerms: $acceptedTerms
        acceptedDsgvo: $acceptedDsgvo
        isReclamation: $isReclamation
        reclamationRepairNumber: $reclamationRepairNumber
        userEmail: $userEmail
        repairPieces: $repairPieces
        additionalInfo: $additionalInfo
      }
    ) {
      __typename
    }
  }
`;

const OrderRepairs: NextPage = () => {
  const [isMounted, setIsMounted] = useState(false);
  const { userData, isAdmin, isColosseumManager } = useUserContext();
  const { refetchSingle, refetch } = useRepairContext();
  const [checkUserExists, userExistsResponse] = useMutation(USER_EXISTS);
  const [createRepair, createRepairResponse] = useMutation(isAdmin ? CREATE_REPAIR : CREATE_REPAIR_USER, {
    onCompleted() {
      refetch();
    },
  });

  const [createUser] = useMutation(CREATE_USER);

  const [repairItemCount, setRepairItemCount] = useState(1);
  const { formValues, setFormValues, register, unregister, setValue } = useFormHook();

  const userExists = useMemo(() => {
    if (userExistsResponse && userExistsResponse.data) {
      return userExistsResponse.data.userExists.exists;
    }
    return !userExistsResponse.called;
  }, [userExistsResponse.data]);

  useEffect(() => {
    setIsMounted(true);
  }, []);
  if (!isMounted) {
    return null;
  }

  const belongsTo = getThemeFromDomain(location.host);
  const isDesos = belongsTo !== "winkelstueck-reparatur";
  const isColosseum = belongsTo === "colosseum";
  const privacyPage = isDesos
    ? "https://desos.eu/datenschutz"
    : "https://winkelstueck-reparatur.de/datenschutzhinweis/";
  const agbPage = isDesos ? "https://desos.eu/agb" : "https://winkelstueck-reparatur.de/agb/";
  const domain = isDesos ? "desos.eu" : "winkelstueck-reparatur.de";

  const RegisterFormProps = { register, unregister, setValue, formValues };

  const BaseRequiredProps = {
    errorMessage: "Dies ist ein Pflichtfeld",
    required: true,
    ...RegisterFormProps,
  };

  const hasManagedUsers = userData && isColosseumManager && userData.managedUsers.length > 0;

  const Form = (
    <div className="max-w-5xl mx-auto">
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          const data = dot.object(formValues);

          if (!userExists) {
            await createUser({ variables: { ...data.user, isDesos, isColosseum } });
          }
          let userEmail;
          if (!data.user) {
            userEmail = userData.email;
          } else {
            userEmail = data.user.email;
          }
          const repairPieces = { data: data.repairPieces };
          const { discountCode, acceptedTerms, acceptedDsgvo, isReclamation, reclamationRepairNumber, additionalInfo } =
            data;
          const res = await createRepair({
            variables: {
              ...(isAdmin ? { sendEmail: false } : {}),
              userEmail,
              repairPieces,
              discountCode,
              acceptedTerms,
              acceptedDsgvo,
              isReclamation,
              reclamationRepairNumber,
              additionalInfo,
            },
          });
          if (isAdmin) {
            refetchSingle(res.data.insert_repair.returning[0].id);
          }
        }}
      >
        {(!userData || isAdmin) && (
          <Input
            id="user.email"
            label="E-Mail"
            errorMessage="Bitte geben Sie eine gültige E-Mail Addresse ein"
            validate={validateEmail}
            required
            onBlur={(event) => {
              if (event.target.value) {
                checkUserExists({ variables: { email: event.target.value } });
              }
            }}
            {...RegisterFormProps}
          />
        )}
        {hasManagedUsers && (
          <Select
            className=""
            options={[{ ...userData }, ...userData.managedUsers].map((user) => {
              const { email, city, street } = user;
              return { label: [email, city, street].join(", "), value: email };
            })}
            id="user.email"
            label="Praxis"
            placeholder="Bitte wählen Sie einen Standort aus"
            {...BaseRequiredProps}
          />
        )}
        {!userExists && (
          <>
            <p>Es ist noch kein Nutzerkonto mit dieser E-Mail vorhanden. Bitte geben Sie folgende Daten ein.</p>
            <Input id="user.office" label="Praxisname" {...BaseRequiredProps} />
            <div className="flex flex-wrap -mx-4">
              <Input className="w-1/2 px-4" id="user.firstName" label="Vorname" {...BaseRequiredProps} />
              <Input className="w-1/2 px-4" id="user.lastName" label="Nachname" {...BaseRequiredProps} />

              <Input className="w-1/2 px-4" id="user.phone" label="Telefon" {...BaseRequiredProps} />
              <Input className="w-1/2 px-4" id="user.street" label="Straße und Hausnummer" {...BaseRequiredProps} />
              <Input className="w-1/2 px-4" id="user.zip" label="Postleitzahl" {...BaseRequiredProps} />
              <Input className="w-1/2 px-4" id="user.city" label="Stadt" {...BaseRequiredProps} />
              <Input className="w-1/2 px-4" id="user.state" label="Bundesland/Provinz/Region" {...BaseRequiredProps} />
              <Select
                className="w-1/2 px-4"
                options={[
                  { label: "Deutschland", value: "DE" },
                  { label: "Österreich", value: "AT" },
                  { label: "Schweiz", value: "CH" },
                  { label: "Liechtenstein", value: "LI" },
                  { label: "Italien", value: "IT" },
                  { label: "Dänemark", value: "DK" },
                ]}
                id="user.country"
                label="Land"
                placeholder="Land auswählen"
                {...BaseRequiredProps}
              />
              <Select
                className="w-1/2 px-4"
                options={[
                  {
                    label: "Über eine Empfehlung",
                    value: "Über eine Empfehlung",
                  },
                  {
                    label: "Über Google-Werbung",
                    value: "Über Google-Werbung",
                  },
                  {
                    label: "Über eine Werbeanzeige",
                    value: "Über eine Werbeanzeige",
                  },
                  {
                    label: "Eine Veranstaltung/Messe",
                    value: "Eine Veranstaltung/Messe",
                  },
                  {
                    label: "Social Media",
                    value: "Social Media",
                  },
                  {
                    label: "Bereits Kunde",
                    value: "Bereits Kunde",
                  },
                ]}
                id="user.referred"
                label="Wie sind Sie auf uns aufmerksam geworden?"
                placeholder="Bitte auswählen"
                {...RegisterFormProps}
                withMisc
              />
            </div>
          </>
        )}
        {[...Array(repairItemCount)].map((_, index) => (
          <RepairPiece
            key={index}
            id={`repairPieces[${index}]`}
            index={index}
            baseProps={BaseRequiredProps}
            delete={index > 0 && repairItemCount - 1 === index && (() => setRepairItemCount(repairItemCount - 1))}
          />
        ))}
        <div className="mt-3 mb-10">
          <button
            type="button"
            className="flex items-center justify-center w-full p-2 font-bold text-center bg-gray-300 hover:bg-gray-400"
            onClick={() => setRepairItemCount(repairItemCount + 1)}
          >
            Instrument hinzufügen&nbsp;
            <GrAdd />
          </button>
        </div>
        <Textarea id="additionalInfo" label="Zusätzliche Infos" {...RegisterFormProps} />

        <Checkbox
          className="my-4"
          id="isReclamation"
          label="Bei dem Auftrag handelt es sich um eine Reklamation"
          asHtml
          {...RegisterFormProps}
        />
        {formValues.isReclamation && (
          <Input
            className="w-1/2"
            id="reclamationRepairNumber"
            label="Ursprüngliche Reparaturnummer für den Reklamationsfall (auffindbar auf der Rechnung)"
            {...RegisterFormProps}
          />
        )}
        <Input id="discountCode" className="max-w-xs" label="Gutscheincode" {...RegisterFormProps} />
        <Checkbox
          className="my-4"
          id="acceptedDsgvo"
          label={`Meine Daten dürfen verwendet werden gemäß DSGVO, sowie der <a class='underline font-semibold text-primary' href='${privacyPage}' target='_blank'>Datenschutzhinweis</a> von ${domain}`}
          asHtml
          {...BaseRequiredProps}
        />
        <Checkbox
          className="my-4"
          id="acceptedTerms"
          label={`Ich habe die <a class='underline font-semibold text-primary' href='${agbPage}' target='_blank'>Geschäftsbedingungen</a> gelesen und stimme denen zu`}
          asHtml
          {...BaseRequiredProps}
        />
        <Button type="submit">Reparatur beantragen</Button>
      </form>
    </div>
  );

  const Success = (
    <div className="max-w-3xl px-8 py-6 mx-auto overflow-hidden bg-white rounded shadow-md ">
      <div className="px-6 py-4">
        <p className="text-base text-gray-700">
          Sehr geehrte(r) Kunde, Ihre Auftrag wurde erfolgreich übernommen. Sie erhalten in Kürze von uns eine
          Bestätigungs-E-Mail. Mit freundlichen Grüssen, Ihr "winkelstueck-reparatur.de" Team
        </p>
      </div>
    </div>
  );
  return createRepairResponse.called ? Success : Form;
};

export default withApollo()(OrderRepairs);
